import {
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  Flex,
  Box,
  Spinner,
  Center,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";

// Why an iframe with srcDoc instead of the script-injection approach?
//
// Viator's partner widget script (https://www.viator.com/orion/partner/widget.js)
// attaches state to `window` the first time it runs — both a global
// flag/registry and a record of which divs it has already rendered into.
// On the second invocation in the same window it sees that state and
// silently no-ops. We tried:
//   1. Removing & re-injecting the script tag → script content runs but
//      bails out on its own globals.
//   2. Cache-busting the URL with a query param → browser fetches fresh
//      bytes but it's still the same code hitting the same globals.
//   3. Forcing a fresh container <div> via React `key` → didn't help
//      because the script's tracking lives on `window`, not on the div.
//
// The only reliable fix is a fresh `window`. An iframe gives us that:
// each open mounts a new iframe with `srcDoc` containing Viator's
// official snippet (per their partner docs). When the drawer closes
// React unmounts the iframe, the document and window are destroyed,
// and any state Viator left behind goes with it. The next open builds
// a brand-new iframe and the script runs from scratch.
//
// NOTE: this is NOT the broken `widget/partner/<pid>/<ref>?widgetPreview=…`
// URL we had before — that was Viator's internal preview page that was
// never meant to be embedded. Here we own the iframe document and use
// the supported `data-vi-*` div + script.js integration inside it.
const VIATOR_SCRIPT_SRC = "https://www.viator.com/orion/partner/widget.js";

// Message type the in-iframe observer posts to the parent once Viator's
// content has actually rendered. Kept distinct so we don't collide with
// any other window message traffic.
const WIDGET_READY_MESSAGE = "viator-widget-rendered";

const buildSrcDoc = (partnerId, widgetIdentifier) => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
<style>
  html, body {
    margin: 0;
    padding: 0;
    background: transparent;
    -webkit-overflow-scrolling: touch;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
</style>
</head>
<body>
  <div data-vi-partner-id="${partnerId}" data-vi-widget-ref="${widgetIdentifier}"></div>
  <script async src="${VIATOR_SCRIPT_SRC}"></script>
  <script>
    (function () {
      // Watch the partner div for the moment Viator's script paints
      // content into it, then post back to the parent so the parent can
      // hide its loading spinner. We can't reach this signal from the
      // parent directly because Viator's script runs inside this
      // iframe's window — but inside the iframe we have a clean view
      // of the DOM mutations.
      var div = document.querySelector('[data-vi-partner-id]');
      if (!div || !window.parent) return;

      var notify = function () {
        try {
          window.parent.postMessage({ type: '${WIDGET_READY_MESSAGE}' }, '*');
        } catch (_) {}
      };

      var observer = new MutationObserver(function () {
        if (div.children.length > 0 || div.innerHTML.trim() !== '') {
          observer.disconnect();
          notify();
        }
      });
      observer.observe(div, { childList: true, subtree: true });

      // Fallback so the parent never gets stuck spinning if Viator's
      // script fails to load (ad blocker, network error).
      setTimeout(function () {
        observer.disconnect();
        notify();
      }, 8000);
    })();
  </script>
</body>
</html>`;

export const ViatorModal = ({ isOpen, onClose, id, viatorID, viatorLink }) => {
  // Backward compatibility: if only `id` is provided, treat it as the
  // widget ref. Older callers passed (id, viatorID); newer ones pass
  // (viatorID, viatorLink).
  const partnerId = viatorID || "U00582589";
  const widgetIdentifier = viatorLink || id;

  // Memoize so we don't rebuild the srcDoc on every render — only when
  // the partner/widget identifiers actually change.
  const srcDoc = useMemo(
    () =>
      widgetIdentifier ? buildSrcDoc(partnerId, widgetIdentifier) : null,
    [partnerId, widgetIdentifier]
  );

  // Spinner stays visible until the in-iframe observer posts back that
  // Viator has rendered content. Reset to false on every open so the
  // spinner reappears for the new iframe.
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setWidgetReady(false);
      return undefined;
    }

    const handler = (event) => {
      if (event?.data?.type === WIDGET_READY_MESSAGE) {
        setWidgetReady(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isOpen]);

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent
        height="82vh"
        maxH="82vh"
        zIndex={100}
        display="flex"
        flexDir="column"
        borderTopLeftRadius="16px"
        borderTopRightRadius="16px"
        overflow="hidden"
        maxW={{ base: "100%", md: "860px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex
          mx={3}
          my={4}
          alignItems="center"
          justifyContent="space-between"
          flexShrink={0}
        >
          <Text mr={4} fontSize="18px" fontWeight={800}>
            Local Experiences
          </Text>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>
        <DrawerBody
          p={0}
          position="relative"
          // The iframe owns its own scroll context; locking the parent
          // prevents iOS Safari from getting confused about which
          // surface should respond to touch.
          overflow="hidden"
        >
          {srcDoc && isOpen ? (
            <>
              {/* Spinner sits over the iframe area while it boots. The
                  in-iframe observer posts back when Viator has actually
                  painted content into the partner div, at which point
                  we drop this overlay. White background so we hide the
                  iframe's progressive load behind a clean surface. */}
              {!widgetReady && (
                <Center
                  position="absolute"
                  inset={0}
                  bg="white"
                  zIndex={2}
                  pointerEvents="none"
                >
                  <Spinner size="lg" color="#817EFF" thickness="3px" />
                </Center>
              )}

              <Box
                as="iframe"
                // We could rely on conditional mounting (isOpen guard)
                // for state isolation, but Chakra's Drawer can keep
                // content mounted across animations. `key` tied to the
                // identifiers guarantees a brand-new iframe — and a
                // brand-new window — every time the drawer opens.
                key={`viator-${partnerId}-${widgetIdentifier}-${isOpen}`}
                srcDoc={srcDoc}
                title="Local Experiences"
                position="relative"
                zIndex={1}
                width="100%"
                height="100%"
                border="none"
                background="transparent"
                allow="popups; popups-to-escape-sandbox"
              />
            </>
          ) : (
            <Box p={4}>
              <Text fontSize="14px">
                Viator experiences are currently unavailable.
              </Text>
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
